import Chart from "Chart";
import Backbone from "backbone";
import * as preact from "preact";

import * as currentDate from "currentDate";
import * as deviceDetect from "deviceDetect";
import * as localAPI from "localAPI";
import * as modalDialog from "modalDialog";
import models from "models/models";
import * as notificationUtils from "notificationUtils";
import * as practiceUtils from "practiceUtils";
import readlangClient from "readlangClient";
import * as dateUtils from "shared/dateUtils";
import * as languageMap from "shared/languageMap";
import * as spacedRepetition from "shared/spacedRepetition";
import * as utils from "shared/utils";
import * as signupModal from "signupModal";
import { SoundEffectView } from "soundEffect";
import * as urlUtils from "urlUtils";

/** Persist selectedBookID only during this session */
let selectedBookID: string | undefined;

interface Props {
  appRouter: any;
}

type State =
  | {
      type: "loading";
      sessionComplete: boolean;
      newPracticeComplete: boolean;
      perfectSession: boolean;
    }
  | {
      type: "loaded";
      language: string;
      firstLanguage: string;
      sessionComplete: boolean;
      newPracticeComplete: boolean;
      perfectSession: boolean;
      hasNewPracticeModes: boolean;
      date: Date;
      todaysCards: any;
      groupCounts: any;
      readyToTest: Backbone.Collection;
      localMode: boolean;
      books: any[];
      showDailyReminderPrompt?: boolean;
      selectedBookID: string;
    };

class LearnPageView extends preact.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    models.user.on("change", this.onUserChange);

    this.state = {
      type: "loading",
      sessionComplete: urlUtils.getUrlVar("sessionComplete") === "true",
      newPracticeComplete: urlUtils.getUrlVar("newPractice") === "true",
      perfectSession: urlUtils.getUrlVar("perfectSession") === "true",
    };

    this.props.appRouter.navigate("/learn", { replace: true });
    this.initialize();
  }

  initialize = async () => {
    this.setState({
      type: "loading",
    });

    const [date, localMode, groupCounts, groups, books] = await Promise.all([
      currentDate
        .getSnapped(models.user.get("updateHour"))
        .then((snappedDate) => new Date(snappedDate.getTime() + 1000)),
      localAPI.isLocalMode(),
      models.userWords.getGroupCounts(true),

      // Note: `reviewedToday` is not available in groups.reviewedToday. Need to call
      // models.userWords.completedToday() instead
      models.userWords.getGroups(["reviewedToday", "readyToTest"]),
      (async () => {
        if (await readlangClient.isLocalMode()) {
          return [];
        }
        return (
          await readlangClient.request("GET", "/api/booksWithWords", {
            wordGroup: "readyToTest",
            language: models.user.get("currentLearningLanguage"),
          })
        ).data;
      })(),
    ]);

    const todaysCards = await models.userWords.completedToday();

    // Prep books
    books.sort((a, b) => b.count - a.count);

    for (const book of books) {
      book.shortTitle = utils.elide(book.title, 40);
      if (book.author) {
        book.shortAuthor = utils.elide(book.author, 20);
      }
    }

    if (selectedBookID && !books.some((book) => book._id === selectedBookID)) {
      selectedBookID = undefined;
    }

    await this.setState({
      type: "loaded",
      language: models.user.get("currentLearningLanguage"),
      firstLanguage: models.user.get("firstLanguage"),
      sessionComplete: this.state.sessionComplete,
      newPracticeComplete: this.state.newPracticeComplete,
      perfectSession: this.state.perfectSession,
      date,
      todaysCards,
      localMode,
      groupCounts,
      readyToTest: groups.readyToTest,
      books,
      showDailyReminderPrompt: !notificationUtils.has(
        "dailyReminder",
        models.user.get("currentLearningLanguage")
      ),
      selectedBookID,
      hasNewPracticeModes: await practiceUtils.hasNewPracticeModes(models.user),
    });

    this.fetchAndRenderRecallHistory();
  };

  componentWillUnmount() {
    models.user.off("change", this.onUserChange);
  }

  /** This renders by direct DOM manipulation as opposed to using preact. */
  fetchAndRenderRecallHistory = async () => {
    if (await readlangClient.isLocalMode()) {
      return;
    }

    // Calculate 7 full days (midnight to midnight, local time) up to and including today
    const now = new Date();
    const todayLocalMidnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const ranges = [];
    for (let i = 6; i >= 0; i--) {
      const start = new Date(
        todayLocalMidnight.getFullYear(),
        todayLocalMidnight.getMonth(),
        todayLocalMidnight.getDate() - i
      );
      const end = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate() + 1
      );
      ranges.push({ start: start.getTime(), end: end.getTime() });
    }
    const recallHistory: { start: number; end: number; recallCount: number }[] =
      (
        await readlangClient.request(
          "POST",
          "/api/userWords/recallHistoryByRanges",
          {
            ranges,
            language: models.user.get("currentLearningLanguage"),
          }
        )
      ).data.recallHistory;

    const days =
      "Sunday Monday Tuesday Wednesday Thursday Friday Saturday".split(" ");

    const chartData: {
      labels: string[];
      datasets: {
        fillColor: string;
        strokeColor: string;
        pointColor: string;
        pointStrokeColor: string;
        data: number[];
      }[];
    } = {
      labels: [],
      datasets: [
        {
          fillColor: "rgba(151,187,235,0.5)",
          strokeColor: "rgba(151,187,235,1)",
          pointColor: "rgba(151,187,235,1)",
          pointStrokeColor: "#fff",
          data: [],
        },
      ],
    };
    let maxCount = 0;
    for (const recall of recallHistory) {
      const day = days[new Date(recall.start).getDay()];
      chartData.labels.push(day);
      chartData.datasets[0].data.push(recall.recallCount);
      if (recall.recallCount > maxCount) {
        maxCount = recall.recallCount;
      }
    }

    if (maxCount > 0) {
      $("#weeklyChartContainerContainer").show();

      const weeklyChartElement = $("#weeklyChart");
      if (weeklyChartElement.length === 0) {
        // This sometimes happens, perhaps in browsers which don't support canvas?
        return;
      }

      if (deviceDetect.isSmall()) {
        weeklyChartElement.attr("width", 288);
      }
      new Chart((weeklyChartElement[0] as any).getContext("2d")).Line(
        chartData,
        {
          scaleOverride: true,
          scaleSteps: 4,
          scaleStepWidth: Math.ceil(Math.max(20, maxCount) / 4),
          scaleStartValue: 0,
          bezierCurve: false,
        }
      );
    }
  };

  onUserChange = () => {
    if (
      this.state.type === "loaded" &&
      (models.user.get("currentLearningLanguage") !== this.state.language ||
        models.user.get("firstLanguage") !== this.state.firstLanguage)
    ) {
      // Re-fetch all data on language change
      this.initialize();
    } else {
      this.forceUpdate();
    }
  };

  getDeckSizes = (cardsAvailable) => {
    const result: {
      verySmallDeckSize?: number;
      smallDeckSize?: number;
      mediumDeckSize?: number;
      largeDeckSize?: number;
      veryLargeDeckSize?: number;
    } = {};

    if (cardsAvailable < 11) {
      result.verySmallDeckSize = Math.min(4, cardsAvailable);
      result.smallDeckSize = Math.min(8, cardsAvailable);
    } else if (cardsAvailable < 20) {
      result.verySmallDeckSize = 4;
      result.smallDeckSize = 8;
      result.mediumDeckSize = cardsAvailable;
    } else if (cardsAvailable < 35) {
      result.verySmallDeckSize = 4;
      result.smallDeckSize = 8;
      result.mediumDeckSize = 15;
      result.largeDeckSize = Math.min(25, cardsAvailable);
    } else {
      result.verySmallDeckSize = 4;
      result.smallDeckSize = 8;
      result.mediumDeckSize = 15;
      result.largeDeckSize = 25;
      result.veryLargeDeckSize = Math.min(50, cardsAvailable);
    }
    return result;
  };

  isDailyReminderEnabled = () => {
    return notificationUtils.has(
      "dailyReminder",
      models.user.get("currentLearningLanguage")
    );
  };

  render() {
    const user = models.user;

    /** This will be empty if no message is meant to be shown */
    const sessionCompleteMessage = (
      <>
        {this.state.sessionComplete ? (
          <>
            {this.state.perfectSession ? (
              <h2 class="perfectSessionMessage">⭐ Perfect Session! ⭐</h2>
            ) : (
              <h2>🎉 Session complete! 🎉</h2>
            )}
          </>
        ) : null}
      </>
    );

    if (this.state.type === "loading") {
      return (
        <>
          {sessionCompleteMessage}
          <div>
            <img class="throbber" src="/content/throbber.gif" />
          </div>
        </>
      );
    }

    const {
      todaysCards,
      localMode,
      date,
      groupCounts,
      readyToTest,
      books,
      showDailyReminderPrompt,
    } = this.state;

    const categories: {
      name: string;
      count: number;
      delta: number;
    }[] = [];
    const totalToday = todaysCards.length;
    const newCardsAvailable = readyToTest.length > 0;
    const {
      verySmallDeckSize,
      smallDeckSize,
      mediumDeckSize,
      largeDeckSize,
      veryLargeDeckSize,
    } = this.getDeckSizes(readyToTest.length);
    const totalCount = groupCounts.notStarted + groupCounts.scheduled;

    let userWords: (UserWord & {
      nextTimeSortKey: number;
      lastSeenSortKey: number;
      nextTime: number;
    })[] = [];

    for (const userWord of todaysCards) {
      const userWordJSON = userWord.toJSON();

      let lastSeen = userWordJSON.previousIntervalDate;
      if (lastSeen) {
        lastSeen = new Date(
          new Date(
            dateUtils.parse(userWordJSON.previousIntervalDate)
          ).getTime() - userWordJSON.previousInterval
        );

        userWordJSON.lastSeenSortKey = lastSeen.getTime();
        lastSeen = Math.round(
          (date.getTime() - lastSeen.getTime()) / spacedRepetition.DAY
        );
        if (lastSeen <= 0) {
          userWordJSON.lastSeen = "New";
        } else if (lastSeen === 1) {
          userWordJSON.lastSeen = "Yesterday";
        } else {
          userWordJSON.lastSeen = lastSeen + " days ago";
        }
      } else {
        userWordJSON.lastSeen = "New";
      }
      userWordJSON.rememberedFirstTime = "Yes";
      const nextDate = new Date(dateUtils.parse(userWordJSON.nextDate));
      if (nextDate) {
        userWordJSON.nextTimeSortKey = nextDate.getTime();
        const nextTime = Math.round(
          (nextDate.getTime() - date.getTime()) / spacedRepetition.DAY
        );
        if (nextTime < 0) {
          userWordJSON.nextTime = "Now";
        } else if (nextTime === 0) {
          userWordJSON.nextTime = "Today";
        } else if (nextTime === 1) {
          userWordJSON.nextTime = "Tomorrow";
          if (lastSeen) {
            userWordJSON.rememberedFirstTime = "No";
          }
        } else {
          userWordJSON.nextTime = "In " + nextTime + " days";
        }
      }

      userWords.push(userWordJSON);
    }

    userWords = userWords.sort((a, b) => {
      const result = a.nextTimeSortKey - b.nextTimeSortKey;
      if (result === 0) {
        if (!a.lastSeenSortKey) {
          return -1;
        }
        if (!b.lastSeenSortKey) {
          return 1;
        }
        return a.lastSeenSortKey - b.lastSeenSortKey;
      } else {
        return result;
      }
    });

    const message =
      totalToday > 0
        ? `You've Recalled ${totalToday} Word${totalToday === 1 ? "" : "s"} Today. Nice Work!`
        : undefined;

    const categoryMap = {
      justStarted: "Working on these",
      gettingThere: "Remembered a day",
      mastered: "Remembered a week",
    };

    for (const [category, displayName] of Object.entries(categoryMap)) {
      const categoryData = {
        name: displayName,
        count: groupCounts[category],
        delta: todaysCards.deltas[category],
      };
      if (categoryData.delta >= 0) {
        categoryData.delta = "+" + categoryData.delta;
      }
      categories.push(categoryData);
    }

    const totalExists = totalToday > 0;

    const dailyReminderEnabled = this.isDailyReminderEnabled();

    const sessionCompleteAudio = this.state.sessionComplete ? (
      <SoundEffectView soundEffect="session-complete" />
    ) : null;

    if (todaysCards.length === 0 && readyToTest.length === 0) {
      return (
        <>
          <p class="hintArrow">
            <img src="/content/upArrow.png" style="margin-bottom:20px"></img>
            <span style="margin-left:15px;max-width:400px;position:absolute;bottom:0">
              Read articles from the{" "}
              <strong>
                <i class="icon-book"></i> Library
              </strong>{" "}
              first, all the words and phrases you translate will be turned into
              flashcards for you to practice here.
            </span>
          </p>
          {sessionCompleteAudio}
        </>
      );
    }

    const practiceSettingsRows: preact.JSX.Element[] = [];

    if (this.state.hasNewPracticeModes) {
      practiceSettingsRows.push(
        <tr>
          <td>
            <label>Mode:</label>
          </td>
          <td>
            <select
              value={this.practiceType()}
              onChange={(event) =>
                user.save(
                  { practiceType: event.currentTarget.value },
                  { patch: true }
                )
              }
            >
              {this.state.hasNewPracticeModes ? (
                <>
                  <option value="blitz">Blitz (no typing)</option>
                  <option value="new">Mastery (with typing)</option>
                </>
              ) : null}
              <option value="classic">Classic Flashcards</option>
              {!this.state.hasNewPracticeModes ? (
                <>
                  <option value="blitz">Blitz (no typing)</option>
                  <option value="new">Mastery (with typing)</option>
                </>
              ) : null}
            </select>
          </td>
        </tr>
      );
    }
    if (mediumDeckSize) {
      practiceSettingsRows.push(
        <tr>
          <td>
            <label>Length:</label>
          </td>
          <td>
            <select
              value={user.get("practiceLength") ?? "small"}
              onChange={(event) =>
                user.save(
                  { practiceLength: event.currentTarget.value },
                  { patch: true }
                )
              }
            >
              <option value="verySmall">{verySmallDeckSize} words</option>
              {smallDeckSize ? (
                <option value="small">{smallDeckSize} words</option>
              ) : null}
              {mediumDeckSize ? (
                <option value="medium">{mediumDeckSize} words</option>
              ) : null}
              {largeDeckSize ? (
                <option value="large">{largeDeckSize} words</option>
              ) : null}
              {veryLargeDeckSize ? (
                <option value="veryLarge">{veryLargeDeckSize} words</option>
              ) : null}
            </select>
          </td>
        </tr>
      );
    }
    if (books.length > 0) {
      practiceSettingsRows.push(
        <tr>
          <td>
            <label>Source:</label>
          </td>
          <td>
            <select
              value={this.state.selectedBookID ?? "all"}
              onChange={async (event) => {
                if (this.state.type === "loading") {
                  throw Error("Unexpected state");
                }

                selectedBookID = event.currentTarget.value;

                this.setState({
                  selectedBookID:
                    selectedBookID === "all" ? undefined : selectedBookID,
                });
              }}
            >
              <option value="all">({totalCount} words ready) All Words</option>
              {books.map((book) => (
                <option data-count={book.count} value={book._id}>
                  ({book.count} words ready) {book.shortTitle},{" "}
                  {book.shortAuthor ?? ""}
                </option>
              ))}
            </select>
          </td>
        </tr>
      );
    }

    return (
      <div>
        {sessionCompleteMessage}
        {sessionCompleteAudio}

        {newCardsAvailable ? (
          <h2>
            {!totalExists ? "Review Your Words" : message}
            {this.state.todaysCards.length > 0 ? (
              <>
                <br />
                <small>
                  <i>Don't Stop There, Keep Learning...</i>
                </small>
              </>
            ) : null}
          </h2>
        ) : (
          <h2>No New Cards</h2>
        )}

        {newCardsAvailable ? (
          <div class="practiceControls">
            <a
              href={practiceUtils.buildSessionUrl(
                user,
                this.state.selectedBookID
              )}
              class="startFlashcardsButton"
            >
              <img src="/content/flashcardsIcon.svg" />
              Start Practice
            </a>
            {practiceSettingsRows.length > 0 ? (
              <div>
                <table class="practiceOptionsTable">
                  <tbody>{practiceSettingsRows}</tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : null}

        {newCardsAvailable && (
          <div id="weeklyChartContainerContainer" style={{ display: "none" }}>
            <h2>
              Practice <strong>Every Day</strong>
            </h2>

            <div id="weeklyChartContainer">
              <canvas id="weeklyChart" width="470" height="200"></canvas>
            </div>
          </div>
        )}

        {!localMode && newCardsAvailable && showDailyReminderPrompt && (
          <div class="callout">
            <label>
              <input
                type="checkbox"
                name="dailyReminder"
                checked={dailyReminderEnabled}
                onClick={() => {
                  this.toggleDailyReminder();
                }}
              ></input>{" "}
              Email me a daily reminder if I forget to practice.
            </label>
          </div>
        )}

        {totalExists && localMode && (
          <div class="callout">
            <a
              href="#"
              class="clickable"
              data-clickable="signUpButton"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                signupModal.show({
                  models,
                });
              }}
            >
              Sign Up For Free
            </a>{" "}
            to practice from another laptop, phone or tablet.
          </div>
        )}

        {totalExists && (
          <div>
            <h2>
              Reviewed {totalToday} Word{totalToday === 1 ? "" : "s"} Today
            </h2>
            <table>
              <thead>
                <tr>
                  <th>Word</th>
                  <th>Translation</th>
                  <th>Next Due</th>
                </tr>
              </thead>
              <tbody>
                {userWords.map((word, index) => (
                  <tr key={index}>
                    <td>{word.word}</td>
                    <td>{word.translation}</td>
                    <td>{word.nextTime}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  practiceType = () => {
    if (this.state.type === "loading") {
      throw Error("Loading");
    }

    return (
      models.user.get("practiceType") ??
      // Defaulting to "blitz" in newPractice experiment (since 2024-06-05)
      (this.state.hasNewPracticeModes ? "blitz" : "classic")
    );
  };

  toggleDailyReminder = () => {
    if (this.isDailyReminderEnabled()) {
      notificationUtils.removeType("dailyReminder");
    } else {
      notificationUtils.addType("dailyReminder");
      modalDialog.show(
        $(`
  <div class="centralDialog">
    <h2>
      Daily ${
        languageMap.map[models.user.get("currentLearningLanguage")].english
      } Reminder Created
    </h2>
    <p>
      You'll receive an email each day that you don't practice, encouraging you to continue.
    </p>
    <p>
      <small>You can alter this setting from the <a href="/settings">Settings Page</a>.</small>
    </p>
  </div>`)
      );
    }
  };
}

export default LearnPageView;
